import * as RadixCheckbox from '@radix-ui/react-checkbox'
import React from 'react'
import { IconCheckMark, IconCheckSquare, IconCheckSquareSolid, IconSquare } from '../../icons/library'
import { SizeProp } from '@fortawesome/fontawesome-svg-core'
import { Tooltip } from '../simple/tooltip'
interface CheckboxProps {
  /** Whether the checkbox is checked or not */
  checked: boolean
  /** Callback function when the checkbox is checked or unchecked */
  onChecked?: (checked: boolean) => void
  /** Additional class names for styling */
  className?: string
  /** Make the checkbox charcoal grey. Otherwise it is the default browser color */
  charcoal?: boolean
  /** Whether the checkbox is disabled or not. If disabled, the checkbox will be greyed out and not clickable */
  disabled?: boolean
}

/**
 * Controlled checkbox component that renders as a full or empty square icon, rather than a checkbox.
 */
export const IconOnlyCheckbox: React.FC<
  Omit<CheckboxProps, 'onChecked'> & {
    size?: SizeProp
    solid?: boolean
    onChecked?: (checked: boolean, shiftPressed: boolean) => void
  }
> = ({ checked, onChecked = undefined, className, size, disabled, solid = false }) => {
  return checked ? (
    solid ? (
      <IconCheckSquareSolid
        className={`${className} ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        size={size}
        onClick={(e) => {
          if (onChecked) {
            onChecked(false, e.shiftKey)
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      />
    ) : (
      <IconCheckSquare
        className={`${className} ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        size={size}
        onClick={(e) => {
          if (onChecked) {
            onChecked(false, e.shiftKey)
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      />
    )
  ) : (
    <IconSquare
      className={`${className} ${disabled ? 'pointer-events-none opacity-50' : ''}`}
      size={size}
      onClick={(e) => {
        if (onChecked) {
          onChecked(true, e.shiftKey)
          e.preventDefault()
          e.stopPropagation()
        }
      }}
    />
  )
}

/**
 * Controlled checkbox component.
 */
export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ checked, onChecked, className, charcoal, disabled, ...props }, ref) => {
    const color = charcoal ? 'border-charcoal bg-charcoal' : 'border-blue-500 bg-blue-500'
    const colorClass = checked ? color : disabled ? 'bg-slate-100 border-slate-400' : 'border-slate-400'

    return (
      <RadixCheckbox.Root
        ref={ref}
        checked={checked}
        onCheckedChange={onChecked}
        className={`h-[13px] w-[13px] min-w-[13px] rounded-sm border ${colorClass} ${className} ${disabled ? 'pointer-events-none cursor-not-allowed opacity-50' : ''}`}
        disabled={disabled}
        {...props}
      >
        <RadixCheckbox.Indicator className="flex h-full w-full text-white" forceMount={checked ? true : undefined}>
          <IconCheckMark
            style={{
              width: '9px',
              transform: 'rotate(-10deg) translate(1.5px, -1px)',
              strokeWidth: 20,
              stroke: 'currentcolor',
            }}
          />
        </RadixCheckbox.Indicator>
      </RadixCheckbox.Root>
    )
  }
)

/**
 * Checkbox with a label.
 */
export const LabeledCheckbox: React.FC<
  CheckboxProps & {
    /** The label for the checkbox */
    label: React.ReactNode
    /** Should the label be displayed before the checkbox? Default is label after */
    labelBefore?: boolean
    /** Tooltip for the checkbox and label */
    tooltip?: React.ReactNode
    /** Prop passed to the tooltip to determine if you should be able to hover the tooptip content and keep it open */
    allowHoveringTooltip?: boolean
  }
> = ({ label, labelBefore, className, tooltip, allowHoveringTooltip, disabled, ...props }) => {
  const elem = (
    <div
      className={`flex items-center gap-2 text-nowrap ${className} ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
    >
      {labelBefore && label}
      <Checkbox {...props} disabled={disabled} className="" />
      {!labelBefore && label}
    </div>
  )
  return tooltip ? (
    <Tooltip text={tooltip} allowHoveringContent={allowHoveringTooltip}>
      {elem}
    </Tooltip>
  ) : (
    elem
  )
}
